import * as _ from 'underscore';
import { toNativePromise } from '~commonInfra/utils/QUtils';
import { IListFetcher } from '../fetchers/IListFetcher';
import { ListResult } from '../fetchers/ListResult';
import { Filter } from '../helpers/Filter';
import { ISliceable } from '../models/ISliceable';
import { ICompositeFilterClause, ILeafFilterClause } from '../ServerApi/types/IFilterClause';
import { IOrderClause } from '../ServerApi/types/IOrderClause';
import { IPage } from '../ServerApi/types/IPage';

export interface ICountable {
  getCount(): ng.IPromise<number>;
}

export interface IResourceQuery<T> extends ISliceable<T>, ICountable {
  getFirst(): ng.IPromise<T>;
  getFilters(): ILeafFilterClause[];
  getOrder(): IOrderClause[];
  getExtraParams(): any;
  addFilter(filterClause: ILeafFilterClause): IResourceQuery<T>;
  withFilters(filterClauses: ILeafFilterClause[]): IResourceQuery<T>;
  withOrder(order: IOrderClause[]): IResourceQuery<T>;
  withFiltersAndOrder(filterClauses: ILeafFilterClause[], order: IOrderClause[]): IResourceQuery<T>;
  withExtraParams(extraParams?: any): IResourceQuery<T>;
}


/**
 * Base class for resource queries, which implements basic logic to build up and store
 * various order/filter and extra params. Subclasses provide their own implementations
 * for how to convert those stored params to actual queries.
 */
export abstract class BaseParamsBuilderResourceQuery<T> implements IResourceQuery<T> {

  private filterClauses: ILeafFilterClause[] = [];
  private order: IOrderClause[] = [];
  private extraParams: any = {};

  public abstract getFirst(): ng.IPromise<T>;
  public abstract getSlice(start: number, size: number): Promise<ListResult<T>>;
  public abstract getCount(): ng.IPromise<number>;

  // caller should not alter the returned array (this should return ReadonlyArray)
  public getFilters(): ILeafFilterClause[] {
    return this.filterClauses;
  }

  // caller should not alter the returned array (this should return ReadonlyArray)
  public getOrder(): IOrderClause[] {
    return this.order;
  }

  // caller should not alter the returned object
  public getExtraParams() {
    return this.extraParams;
  }

  public withFiltersAndOrder(filterClauses: ILeafFilterClause[], order: IOrderClause[]): this {
    return this.clone().setFilterClauses(filterClauses).setOrder(order);
  }

  public withExtraParams(extraParams?: any): this {
    return extraParams ? this.clone().setExtraParams(extraParams) : this;
  }

  public withFilters(filterClauses: ILeafFilterClause[]): this {
    return this.clone().setFilterClauses(filterClauses);
  }

  public addFilter(filterClause: ILeafFilterClause): this {
    return this.withFilters((this.filterClauses || []).concat([filterClause]));
  }

  public withOrder(order: IOrderClause[]): this {
    return this.clone().setOrder(order);
  }

  private clone(): this {
    return _.clone(this);
  }

  private setFilterClauses(filterClauses: ILeafFilterClause[]): this {
    this.filterClauses = filterClauses;
    return this;
  }

  private setOrder(order: IOrderClause[]): this {
    this.order = order;
    return this;
  }

  private setExtraParams(extraParams: any): this {
    this.extraParams = extraParams;
    return this;
  }
}


/**
 * This is the resource query used for queries backed by standard VizPortal APIs.
 */
export class ResourceQuery<T> extends BaseParamsBuilderResourceQuery<T> {

  constructor(private readonly fetcher: IListFetcher<T>) {
    super();
  }

  public getSlice(start: number, size: number): Promise<ListResult<T>> {

    return toNativePromise(this.fetcher.fetchList(_.extend({
        filter: formatFilterClauses(this.getFilters()),
        order: this.getOrder().length > 0 ? this.getOrder() : undefined,
        page: getPage(start, size)
      }, this.getExtraParams())));
  }

  public getFirst(): Promise<T> {
    return this.getSlice(0, 1).then((result: ListResult<T>) => {
        if (result.items && result.items.length > 0) {
          return result.items[0];
        } else {
          return null;
        }
      }
    );
  }

  public getCount(): Promise<number> {
    return this.getSlice(0, 0).then((slice: ListResult<T>) => slice.total);
  }

  public getFetcher() {
    return this.fetcher;
  }
}


export function getPage(start: number, length: number): IPage {
  return { startIndex: start, maxItems: length };
}

export function formatFilterClauses(filterClauses: ILeafFilterClause[]): ICompositeFilterClause {
  return (filterClauses && filterClauses.length > 0) ? Filter.and(filterClauses) : undefined;
}
